import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { Button } from '../components/shared/Button';
import { Team, Song, SongAnswer } from '../types';
import './Scoring.css';

const Scoring: React.FC = () => {
  const navigate = useNavigate();
  const { 
    teams, 
    currentRound, 
    roundNumber, 
    roundAnswers, 
    updateSongAnswer, 
    calculateRoundResults 
  } = useGame();
  
  // Track which team we're currently scoring
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);

  const currentTeam = teams[currentTeamIndex];
  const roundSongs = currentRound?.songs || [];

  const handleCalculate = () => {
    calculateRoundResults();
    navigate('/admin');
  };

  const getAnswer = (teamId: string, songId: string): SongAnswer => {
    const teamAnswers = roundAnswers[teamId] || {};
    return teamAnswers[songId] || {
      songId,
      position: null,
      yearCorrect: false,
      artistCorrect: false,
      titleCorrect: false,
    };
  };

  // Check if placing the song at a given position is correct
  const checkPlacement = (team: Team, song: Song, positionIndex: number): boolean => {
    const timeline = team.timeline;
    const songYear = song.year;
    
    if (positionIndex === 0) {
      // Before first card
      return songYear <= timeline[0].year;
    } else if (positionIndex >= timeline.length) {
      // After last card
      return songYear >= timeline[timeline.length - 1].year;
    } else {
      // Between two cards
      const beforeCard = timeline[positionIndex - 1];
      const afterCard = timeline[positionIndex];
      return songYear >= beforeCard.year && songYear <= afterCard.year;
    }
  };

  const handlePlacement = (teamId: string, team: Team, song: Song, positionIndex: number) => {
    const isCorrect = checkPlacement(team, song, positionIndex);
    updateSongAnswer(teamId, song.id, { 
      position: positionIndex, 
      yearCorrect: isCorrect 
    });
  };

  const handleBonusAnswer = (teamId: string, songId: string, artistCorrect: boolean, titleCorrect: boolean) => {
    updateSongAnswer(teamId, songId, { artistCorrect, titleCorrect });
  };

  const nextTeam = () => {
    if (currentTeamIndex < teams.length - 1) {
      setCurrentTeamIndex(currentTeamIndex + 1);
    }
  };

  const prevTeam = () => {
    if (currentTeamIndex > 0) {
      setCurrentTeamIndex(currentTeamIndex - 1);
    }
  };

  // Calculate progress
  const getTeamProgress = (teamId: string): number => {
    const teamAnswers = roundAnswers[teamId] || {};
    return Object.keys(teamAnswers).filter(songId => 
      teamAnswers[songId].position !== null
    ).length;
  };

  const getTotalProgress = (): number => {
    let total = 0;
    teams.forEach(team => {
      total += getTeamProgress(team.id);
    });
    return total;
  };

  const isAllScored = (): boolean => {
    return getTotalProgress() >= teams.length * roundSongs.length;
  };

  // Render timeline placement for a single song
  const renderSongPlacement = (team: Team, song: Song, songIndex: number) => {
    const answer = getAnswer(team.id, song.id);
    const hasAnswer = answer.position !== null;
    
    return (
      <div key={song.id} className={`song-scoring-card ${hasAnswer ? 'answered' : ''}`}>
        <div className="song-header">
          <span className="song-number">#{songIndex + 1}</span>
          <div className="song-info">
            <strong>{song.title}</strong>
            <span className="song-meta">{song.artist} ({song.year})</span>
          </div>
          {hasAnswer && (
            <span className={`answer-badge ${answer.yearCorrect ? 'correct' : 'wrong'}`}>
              {answer.yearCorrect ? '✓' : '✗'}
            </span>
          )}
        </div>

        <div className="timeline-placement">
          <label>Waar plaatste het team dit nummer?</label>
          <div className="placement-selector">
            <button
              className={`placement-slot ${answer.position === 0 ? 'selected' : ''} ${answer.position === 0 && answer.yearCorrect ? 'correct' : ''} ${answer.position === 0 && !answer.yearCorrect && hasAnswer ? 'wrong' : ''}`}
              onClick={() => handlePlacement(team.id, team, song, 0)}
            >
              ← Vóór
            </button>
            
            {team.timeline.map((card, index) => (
              <React.Fragment key={index}>
                <span className={`timeline-year ${card.isStart ? 'start' : ''}`}>
                  {card.year}
                </span>
                <button
                  className={`placement-slot ${answer.position === index + 1 ? 'selected' : ''} ${answer.position === index + 1 && answer.yearCorrect ? 'correct' : ''} ${answer.position === index + 1 && !answer.yearCorrect && hasAnswer ? 'wrong' : ''}`}
                  onClick={() => handlePlacement(team.id, team, song, index + 1)}
                >
                  {index < team.timeline.length - 1 ? '↔' : 'Ná →'}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Bonus: Artiest + Titel */}
        {hasAnswer && answer.yearCorrect && (
          <div className="bonus-scoring">
            <label>Bonus: Artiest + Titel correct? (+1 token)</label>
            <div className="answer-buttons">
              <button
                className={`answer-btn correct ${answer.artistCorrect && answer.titleCorrect ? 'selected' : ''}`}
                onClick={() => handleBonusAnswer(team.id, song.id, true, true)}
              >
                🪙 Ja
              </button>
              <button
                className={`answer-btn wrong ${hasAnswer && !(answer.artistCorrect && answer.titleCorrect) ? 'selected' : ''}`}
                onClick={() => handleBonusAnswer(team.id, song.id, false, false)}
              >
                Nee
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!currentRound || roundSongs.length === 0) {
    return (
      <div className="scoring-page">
        <div className="scoring-container">
          <h1>📝 Score Invoer</h1>
          <div className="scoring-info-box">
            <p>Geen actieve ronde gevonden. Start eerst een ronde met nummers.</p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/admin')}>
            ← Terug naar Spelleider
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="scoring-page">
      <div className="scoring-container">
        <h1>📝 Score Invoer - Ronde #{roundNumber}</h1>
        <p className="scoring-subtitle">
          Voer per team in waar ze elk nummer hebben geplaatst
        </p>

        {/* Progress bar */}
        <div className="scoring-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(getTotalProgress() / (teams.length * roundSongs.length)) * 100}%` }}
            />
          </div>
          <span className="progress-text">
            {getTotalProgress()} / {teams.length * roundSongs.length} antwoorden ingevoerd
          </span>
        </div>

        {/* Team tabs */}
        <div className="team-tabs">
          {teams.map((team, index) => {
            const progress = getTeamProgress(team.id);
            const isComplete = progress === roundSongs.length;
            return (
              <button
                key={team.id}
                className={`team-tab ${index === currentTeamIndex ? 'active' : ''} ${isComplete ? 'complete' : ''}`}
                onClick={() => setCurrentTeamIndex(index)}
              >
                <span className="team-tab-name">{team.name}</span>
                <span className="team-tab-progress">
                  {isComplete ? '✓' : `${progress}/${roundSongs.length}`}
                </span>
              </button>
            );
          })}
        </div>

        {/* Current team scoring */}
        {currentTeam && (
          <div className="current-team-scoring">
            <div className="team-scoring-header">
              <h2>{currentTeam.name}</h2>
              <div className="team-timeline-display">
                <span>Timeline: </span>
                {currentTeam.timeline.map((card, i) => (
                  <span key={i} className={`timeline-badge ${card.isStart ? 'start' : ''}`}>
                    {card.year}
                  </span>
                ))}
              </div>
            </div>

            <div className="songs-scoring-list">
              {roundSongs.map((song, index) => renderSongPlacement(currentTeam, song, index))}
            </div>

            {/* Navigation */}
            <div className="team-navigation">
              <Button 
                variant="secondary" 
                onClick={prevTeam}
                disabled={currentTeamIndex === 0}
              >
                ← Vorig Team
              </Button>
              <span className="team-counter">
                Team {currentTeamIndex + 1} van {teams.length}
              </span>
              <Button 
                variant="primary" 
                onClick={nextTeam}
                disabled={currentTeamIndex === teams.length - 1}
              >
                Volgend Team →
              </Button>
            </div>
          </div>
        )}

        {/* Final actions */}
        <div className="scoring-actions">
          <Button variant="secondary" onClick={() => navigate('/admin')}>
            ← Terug (later verder)
          </Button>
          <Button 
            variant="success" 
            size="large" 
            onClick={handleCalculate}
            disabled={!isAllScored()}
          >
            {isAllScored() 
              ? '✓ Bereken Scores & Bekijk Resultaten' 
              : `Nog ${teams.length * roundSongs.length - getTotalProgress()} plaatsingen nodig`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Scoring;
